<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { onMounted, ref, nextTick, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import Swal from "sweetalert2";

import { TabStrip as kTabStrip, type TabStripSelectEventArguments } from '@progress/kendo-vue-layout'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { RadioButton } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns';

import { EventMasterOrderGet, CreateEventAddress, EventShipNowCartSubmit, EventMasterOrderSubmit, AddressAdd } from '../../services/EventService'
import { handleError, AlertCartSubmitResponse, AlertMasterOrderSubmitResponse } from '../../services/ErrorService.ts'
import { ConsultantGet } from '../../api/Consultant.ts'
import { FindAddressBook } from '../../api/AddressBook.ts'
import type { IAoeCart } from '../../types/CartTypes'
import type { IEventOrderResponse } from '../../types/EventTypes'
import type { IAddressVerificationListItem, IAddress } from '../../types/CountryCultureTypes'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { GetFullProductImageDictionary } from '../../services/SkuService.ts'
import { GetOrderReceiptUrl, GetEventManagementUrl, GetEventListUrl } from '../../stores/SettingsStore.ts'

import AoeGuestHeader from '../Controls/AoeGuestHeader.vue'
import AoeTabbedProductEntry from '../Controls/AoeTabbedProductEntry.vue'
import AoeCartItemGrid from '../Controls/AoeCartItemGrid.vue'
import AoeShippingControl from '../Controls/AoeShippingControl.vue'
import AoeOrderTotalsSummary from '../Controls/AoeOrderTotalsSummary.vue'
import AoePaymentControl from '../Controls/AoePaymentControl.vue'
import AoeDiscountControl from '../Controls/AoeDiscountControl.vue'
import AoePartyOrderSummary from '../Controls/AoePartyOrderSummary.vue'
import AoeAddGuestControl from '../Controls/AoeAddGuestControl.vue'
import AddressControl from '../PWS/AddressControl.vue'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import axios from 'axios'
import type { AxiosError } from 'axios'
import type { IErrorResponse } from '@/types/AoeTypes.ts'

// Props removed - consultant cart visibility is now determined by checking if consultant is the host

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AoePartOrderEntry.vue')

document.onreadystatechange = function () {
    if (document.readyState == "complete") {

    }
}

interface Tabs {
  title: string
  content: string
  disabled?: boolean
  contentClassName: string
  titleRender: string,
  id?: string
  index?: number
}

interface TabsProperties {
  index: number
  productImageUrls: Record<string, string>
  defaultCountryCode: string
  cart: IAoeCart
}

interface ConsolidatedAddressOptions {
  text: string
  id: string
}

interface BreadcrumbSegment {
  text: string
  isLink: boolean
  targetTab?: number
}


const selectedConsolidatedAddress = ref<ConsolidatedAddressOptions>({ text: '', id: '' })
const consolidatedAddressOptions = ref<ConsolidatedAddressOptions[]>([])
const showChangeAddressError = ref(false)
const orderTypeId = ref<string>('')
const selectedTab = ref(0);
const selectedProductEntryTab = ref(0)
const selectedProductEntryCategory = ref('')
const tabs = ref<Tabs[]>([])
const tabStrip = ref<Tabs[]>([])
const tabProps = ref<TabsProperties[]>([])
const customerBeeNumber = ref<number>(0)
const eventMO = ref<IEventOrderResponse>()
const showChangeAddress = ref(false)
const changeAddressType = ref<string>('existing')
const radioButtonKey = ref<number>(0)
const eventAddressId = ref<number | null>(0)
const eventAddressFirstName = ref<string>('')
const eventAddressLastName = ref<string>('')
const eventAddressLine1 = ref<string>('')
const eventAddressLine2 = ref<string | null>('')
const eventAddressHouseNumber = ref<string | null>('')
const eventAddressCity = ref<string>('')
const eventAddressStateCode = ref<string | null>('')
const eventAddressPostalCode = ref<string>('')
const eventAddressCountryCode = ref<string>('')
const addressVerificationShow = ref<boolean>(false)
const addressVerifyError = ref<boolean>(false)
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const orderReceiptUrl = ref<string>('')
const eventManagementURL = ref<string>('')
const eventListURL = ref<string>('')

// Guards against out-of-order responses: update-cart can fire in quick succession
// (e.g. saving an address then switching ship-to-type), and a slower earlier
// request resolving after a faster later one must not overwrite the newer cart state.
let latestUpdateCartRequestId = 0

// Computed property to determine the current breadcrumb structure
const breadcrumbStructure = computed((): { segments: BreadcrumbSegment[] } => {
  const currentTab = tabs.value[selectedTab.value]

  if (!currentTab) {
    return { segments: [{ text: t('partyOrderEntry.BreadCrumbPage'), isLink: false }] }
  }

  // Order Summary tab (index 0)
  if (selectedTab.value === 0) {
    return { segments: [{ text: t('partyOrderEntry.BreadCrumbOrderSummary'), isLink: false }] }
  }

  // Guest List Add tab (index 1)
  if (selectedTab.value === 1) {
    return { segments: [{ text: t('partyOrderEntry.BreadCrumbPage'), isLink: false }] }
  }

  // Check if it's a guest cart tab (content: 'GuestCart' and not host)
  if (currentTab.content === 'GuestCart') {
    const tabProp = tabProps.value.find(x => x.index === selectedTab.value)
    if (tabProp?.cart?.isHostCart) {
      // Host order tab
      return {
        segments: [
          { text: t('partyOrderEntry.BreadCrumbHost'), isLink: true, targetTab: 0 },
          { text: t('partyOrderEntry.BreadCrumbOrder'), isLink: false }
        ]
      }
    } else if (tabProp?.cart && !tabProp.cart.isConsultantCart) {
      // Guest order tab
      return {
        segments: [
          { text: t('partyOrderEntry.BreadCrumbGuest'), isLink: true, targetTab: 0 },
          { text: t('partyOrderEntry.BreadCrumbOrder'), isLink: false }
        ]
      }
    }
  }

  // Check if it's a host cart tab (content: 'HostCart')
  if (currentTab.content === 'HostCart') {
    return {
      segments: [
        { text: t('partyOrderEntry.BreadCrumbHost'), isLink: true, targetTab: 0 },
        { text: t('partyOrderEntry.BreadCrumbOrder'), isLink: false }
      ]
    }
  }

  // Default fallback
  return { segments: [{ text: t('partyOrderEntry.BreadCrumbPage'), isLink: false }] }
})

onMounted(async () => {
  logger.debug('onMounted aoePartyOrderEntry')
  const urlParams = new URLSearchParams(window.location.search)
  const cb = urlParams.get('customer');
  if (cb != null) customerBeeNumber.value = Number(cb)

  orderReceiptUrl.value = await GetOrderReceiptUrl()

  GetEventManagementUrl().then(url => {
    eventManagementURL.value = url + '?event='
  })

  GetEventListUrl().then(url => {
    eventListURL.value = url
  })

  LoadMasterOrder()
})

async function LoadMasterOrder()
{
  logger.debug('LoadMasterOrder start')
  const urlParams = new URLSearchParams(window.location.search)
  const eventIdToken = urlParams.get('event')

  if (eventIdToken == null) return

  tabs.value = []
  tabStrip.value = []
  tabProps.value = []
  consolidatedAddressOptions.value = []

  SetIsLoading(true)
  EventMasterOrderGet({
    eventIdToken: eventIdToken
  }).then(async (resp) => {
    const eventResp = resp as IEventOrderResponse
    logger.debug('EventMasterOrderGet', eventResp)
    if (eventResp)
    {
      eventMO.value = eventResp
      orderTypeId.value = eventResp.item.orderTypeId
      tabs.value.push({ title: t('partyOrderEntry.OrderSummary'), titleRender:'OrderSummaryTabStripHeader', content: 'PartyOrderSummary', contentClassName:'party-order-entry-tab party-order-entry-tab-order-summary' })
      tabs.value.push({ title: t('partyOrderEntry.GuestListAdd'), titleRender:'GuestAddTabStripHeader', content: 'AddGuest', contentClassName:'party-order-entry-tab party-order-entry-tab-guest-add' })

      let index = 2

      // Sort guest carts consistently to prevent tab order changes after page reload
      // Sort by: 1) beeNumberCustomer (nulls last), 2) customerFirstName, 3) customerLastName, 4) customerEmailAddress
      const guestCarts = eventResp.item.carts
        .filter(cart => !cart.isHostCart && !cart.isConsultantCart)
        .sort((a, b) => {
          // Sort by beeNumberCustomer first (nulls last)
          if (a.beeNumberCustomer !== null && b.beeNumberCustomer !== null) {
            if (a.beeNumberCustomer !== b.beeNumberCustomer) {
              return a.beeNumberCustomer - b.beeNumberCustomer
            }
          } else if (a.beeNumberCustomer !== null && b.beeNumberCustomer === null) {
            return -1 // a comes first
          } else if (a.beeNumberCustomer === null && b.beeNumberCustomer !== null) {
            return 1 // b comes first
          }

          // Then sort by first name
          const firstNameCompare = (a.customerFirstName || '').localeCompare(b.customerFirstName || '')
          if (firstNameCompare !== 0) return firstNameCompare

          // Then by last name
          const lastNameCompare = (a.customerLastName || '').localeCompare(b.customerLastName || '')
          if (lastNameCompare !== 0) return lastNameCompare

          // Finally by email address for consistent ordering
          return (a.customerEmailAddress || '').localeCompare(b.customerEmailAddress || '')
        })

      for (const guestCart of guestCarts)
      {
        tabs.value.push({ title: guestCart.customerFirstName + ' ' + guestCart.customerLastName, titleRender:'GuestTabStripHeader', content: 'GuestCart', contentClassName:'party-order-entry-tab party-order-entry-tab-guest' ,index:index})

        tabProps.value.push({ index: index,
                              productImageUrls: await GetProductImageUrls(guestCart),
                              defaultCountryCode: eventResp.item.event.defaultCountryCode,
                              cart: guestCart })

        if (guestCart.beeNumberCustomer == customerBeeNumber.value)
        {
          selectedTab.value = index
        }

        index++
      }

      tabs.value.push({ title: t('partyOrderEntry.HostSummary'), titleRender:'HostSummaryTabStripHeader', content: 'HostOrderSummary', contentClassName:'party-order-entry-tab party-order-entry-tab-host-summary' })
      index++

      const hostCart = eventResp.item.carts.find(x => x.isHostCart)
      if (hostCart)
      {
        tabs.value.push({ title: hostCart.customerFirstName + ' ' + hostCart.customerLastName, titleRender:'HostTabStripHeader', content: 'HostCart', contentClassName:'party-order-entry-tab party-order-entry-tab-host',index:index })
        tabProps.value.push({ index: index,
                              productImageUrls: await GetProductImageUrls(hostCart),
                              defaultCountryCode: eventResp.item.event.defaultCountryCode,
                              cart: hostCart  })

        if (hostCart.beeNumberCustomer == customerBeeNumber.value)
        {
          selectedTab.value = index
        }

        // Fetch host's address from the address book instead of using cart shipping address
        try {
          // Search for host in address book by email from event host data
          const response = await FindAddressBook({ searchText: eventResp.item.event.host.email })

          if (response && response.items && response.items.length > 0) {
            const addressBookEntry = response.items[0]
            if (addressBookEntry?.address && addressBookEntry.address.addressId) {
              const hostAddress = addressBookEntry.address
              const houseNumber = hostAddress.houseNumber != null && hostAddress.houseNumber.length > 0 ? ' ' + hostAddress.houseNumber : ''
              const addressLine2 = hostAddress.addressLine2 != null && hostAddress.addressLine2.length > 0 ? hostAddress.addressLine2 + ', ' : ''
              const stateCode = hostAddress.stateCode != null && hostAddress.stateCode.length > 0 ? hostAddress.stateCode + ' ' : ''
              consolidatedAddressOptions.value.push({ text: hostCart.customerFirstName + ' ' + hostCart.customerLastName + ': ' +
                                                            hostAddress.addressLine1 + houseNumber + (addressLine2 ? ', ' + addressLine2.slice(0, -2) : '') +
                                                            ', ' + hostAddress.city + (stateCode ? ', ' + stateCode : ', ') +
                                                            hostAddress.postalCode + ', ' + hostAddress.countryCode + ' (' + t('partyOrderEntry.Host') + ')',
                                                      id: hostAddress.addressId!.toString() })
            } else {
              throw new Error(t('errors.noShippingAddressFoundInAddressBookEntry'))
            }
          } else {
            throw new Error(t('errors.noAddressBookEntryFoundForHostEmail'))
          }
        } catch (error) {
          logger.error('Failed to fetch host address from address book:', error)
          // Fallback to cart shipping address if API call fails or no addressBookId
          const houseNumber = hostCart.shippingAddress.houseNumber != null && hostCart.shippingAddress.houseNumber.length > 0 ? ' ' + hostCart.shippingAddress.houseNumber : ''
          const addressLine2 = hostCart.shippingAddress.addressLine2 != null && hostCart.shippingAddress.addressLine2.length > 0 ? hostCart.shippingAddress.addressLine2 + ', ' : ''
          const stateCode = hostCart.shippingAddress.stateCode != null && hostCart.shippingAddress.stateCode.length > 0 ? hostCart.shippingAddress.stateCode + ' ' : ''
          consolidatedAddressOptions.value.push({ text: hostCart.customerFirstName + ' ' + hostCart.customerLastName + ': ' +
                                                        hostCart.shippingAddress.addressLine1 + houseNumber + (addressLine2 ? ', ' + addressLine2.slice(0, -2) : '') +
                                                        ', ' + hostCart.shippingAddress.city + (stateCode ? ', ' + stateCode : ', ') +
                                                        hostCart.shippingAddress.postalCode + ', ' + hostCart.shippingAddress.countryCode + ' (' + t('partyOrderEntry.Host') + ')',
                                                  id: hostCart.shippingAddress.addressId.toString() })
        }

        index++
      }


        const conCart = eventResp.item.carts.find(x => x.isConsultantCart)
        if (conCart)
        {
          // Fetch consultant's address from the API instead of using cart shipping address
          try {
            const consultantResponse = await ConsultantGet(conCart.beeNumberPayee)
            if (consultantResponse?.item?.shippingAddress) {
              const consultantAddress = consultantResponse.item.shippingAddress
              const houseNumber = consultantAddress.houseNumber != null && consultantAddress.houseNumber.length > 0 ? ' ' + consultantAddress.houseNumber : ''
              const addressLine2 = consultantAddress.addressLine2 != null && consultantAddress.addressLine2.length > 0 ? consultantAddress.addressLine2 + ', ' : ''
              const stateCode = consultantAddress.stateCode != null && consultantAddress.stateCode.length > 0 ? consultantAddress.stateCode + ' ' : ''
              consolidatedAddressOptions.value.push({ text: conCart.customerFirstName + ' ' + conCart.customerLastName + ': ' +
                                                            consultantAddress.addressLine1 + houseNumber + (addressLine2 ? ', ' + addressLine2.slice(0, -2) : '') +
                                                            ', ' + consultantAddress.city + (stateCode ? ', ' + stateCode : ', ') +
                                                            consultantAddress.postalCode + ', ' + consultantAddress.countryCode + ' (' + t('partyOrderEntry.ConsultantName') + ')',
                                                      id: consultantAddress.addressId.toString() })
            }
          } catch (error) {
            logger.error('Failed to fetch consultant address:', error)
            // Fallback to cart shipping address if API call fails
            const houseNumber = conCart.shippingAddress.houseNumber != null && conCart.shippingAddress.houseNumber.length > 0 ? ' ' + conCart.shippingAddress.houseNumber : ''
            const addressLine2 = conCart.shippingAddress.addressLine2 != null && conCart.shippingAddress.addressLine2.length > 0 ? conCart.shippingAddress.addressLine2 + ', ' : ''
            const stateCode = conCart.shippingAddress.stateCode != null && conCart.shippingAddress.stateCode.length > 0 ? conCart.shippingAddress.stateCode + ' ' : ''
            consolidatedAddressOptions.value.push({ text: conCart.customerFirstName + ' ' + conCart.customerLastName + ': ' +
                                                          conCart.shippingAddress.addressLine1 + houseNumber + (addressLine2 ? ', ' + addressLine2.slice(0, -2) : '') +
                                                          ', ' + conCart.shippingAddress.city + (stateCode ? ', ' + stateCode : ', ') +
                                                          conCart.shippingAddress.postalCode + ', ' + conCart.shippingAddress.countryCode + ' (' + t('partyOrderEntry.ConsultantName') + ')',
                                                    id: conCart.shippingAddress.addressId.toString() })
          }

        }

      tabs.value.push({ title: 'Event Location', id:'tabEventLocation', titleRender:'EventLocationTabStripHeader', content: '', disabled: true, contentClassName:'party-order-entry-tab party-order-entry-tab-event-location' })
      index++

      SetIsLoading(false)
      tabStrip.value = tabs.value

      setTimeout(() => {
        const test = document.getElementById('partyOrderEntryTabs') as HTMLElement
        if (test != null)
        {
          const tabsLI = test.children[0].children[0].children as HTMLCollection
          const tabCount: number = tabsLI.length;
          const eventLoc = test.children[0].children[0].children[tabCount - 1] as HTMLElement
          if (eventLoc != null)
          {
            eventLoc.className = 'k-last k-item'
          }
        }
      }, 2000)

      logger.debug('LoadMasterOrder done')
    }
  }).catch(async error => {
    SetIsLoading(false)
    await handleError(error, t)
  })
}

function addEventAddress(event: MouseEvent)
{
  logger.debug('addEventAddress', event, eventAddressLine1.value, eventAddressLine2.value, eventAddressCity.value, eventAddressStateCode.value, eventAddressPostalCode.value, eventAddressCountryCode.value)
  SetIsLoading(true)

  if (changeAddressType.value == 'new')
  {
    addAddress(false).then(() => {
      if (verifyAddress.value.length > 0  || addressVerifyError.value == true) {
        SetIsLoading(false)
        addressVerificationShow.value = true;
      }
      else
      {
        CreateEventAddress(
          {
            eventIdToken: eventMO.value?.item.event.eventIdToken ?? '',
            addressId: eventAddressId.value ?? 0,
            aoeMasterOrderIdToken: eventMO.value?.item.aoeMasterOrderIdToken ?? '',
          }
        ).then(() => {
          LoadMasterOrder()
          closeChangeAddressPopup()
        })
      }
    });
  }
  else
  {
    if (selectedConsolidatedAddress.value.id == '')
    {
      SetIsLoading(false)
      showChangeAddressError.value = true
      return
    }

    CreateEventAddress(
      {
        eventIdToken: eventMO.value?.item.event.eventIdToken ?? '',
        addressId: selectedConsolidatedAddress.value.id != '' ? Number(selectedConsolidatedAddress.value.id) : 0,
        aoeMasterOrderIdToken: eventMO.value?.item.aoeMasterOrderIdToken ?? '',
      }
    ).then(() => {
      LoadMasterOrder()
      closeChangeAddressPopup()
    })
  }
}

async function addAddress(skipAddressValidation: boolean)
{

  verifyAddress.value = []
  const address: IAddress = {
        firstName: eventAddressFirstName.value,
        lastName: eventAddressLastName.value,
        addressLine1: eventAddressLine1.value,
        addressLine2: eventAddressLine2.value,
        houseNumber: eventAddressHouseNumber.value,
        city: eventAddressCity.value,
        stateCode: eventAddressStateCode.value,
        postalCode: eventAddressPostalCode.value,
        countryCode: eventAddressCountryCode.value,
      }

      logger.debug('AddressAdd', address)
      await AddressAdd({
        address: address,
        skipAddressValidation: skipAddressValidation,
      }).then(response => {
        logger.debug('AddressAdd response:', response)
        for (const item of response.items) {
          verifyAddress.value.push(item);
        }

        if (response.addressValidationMessageId != undefined && response.addressValidationMessageId.length > 0)
        {
          addressVerifyError.value = true
        }
        else
        {
          addressVerifyError.value = false
        }

        eventAddressId.value = response.addressId
      })

      if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
        addressVerificationShow.value = true;
      }
      else
      {
        addressVerificationShow.value = false;
      }
}

async function GetProductImageUrls(cart: IAoeCart) : Promise<Record<string, string>>
{
  const uniqueProductCodes = Array<string>()
  for(const cartItem of cart.items) {
    if (!uniqueProductCodes.includes(cartItem.productCode)) {
      uniqueProductCodes.push(cartItem.productCode)
    }
  }
  if (cart.earnedDiscounts != undefined)
  {
    for(const earnedDisc of cart.earnedDiscounts) {
      if (earnedDisc.items != undefined) {
        for(const earnedDiscItem of earnedDisc.items) {
          if (!uniqueProductCodes.includes(earnedDiscItem.productCode)) {
            uniqueProductCodes.push(earnedDiscItem.productCode)
          }
        }
      }
    }
  }
  const pIUrls = await GetFullProductImageDictionary(uniqueProductCodes)
  return pIUrls
}

function onSelectHandler(event: TabStripSelectEventArguments) {
  selectedTab.value = event.selected;
  customerBeeNumber.value = 0
}

function navigateToBreadcrumbTab(targetTab: number) {
  selectedTab.value = targetTab;
  customerBeeNumber.value = 0
}

async function updateCartData() {
  logger.debug('updateCartData - refreshing cart data without full reload')
  const urlParams = new URLSearchParams(window.location.search)
  const eventIdToken = urlParams.get('event')

  if (eventIdToken == null) return

  const requestId = ++latestUpdateCartRequestId

  try {
    const eventResp = await EventMasterOrderGet({
      eventIdToken: eventIdToken
    }) as IEventOrderResponse

    // A newer update-cart request was issued while this one was in flight -
    // its response is stale, so discard it to avoid clobbering fresher cart state.
    if (requestId !== latestUpdateCartRequestId) {
      logger.debug('updateCartData discarding stale response', requestId, latestUpdateCartRequestId)
      return
    }

    if (eventResp) {
      logger.debug('EventMasterOrderGet for update', eventResp)
      eventMO.value = eventResp

      // Update existing cart data in tabProps without rebuilding tabs
      for (const tabProp of tabProps.value) {
        const updatedCart = eventResp.item.carts.find(c => c.aoeCartIdToken === tabProp.cart.aoeCartIdToken)
        if (updatedCart) {
          // Update product image URLs if items changed
          const updatedImageUrls = await GetProductImageUrls(updatedCart)

          // Another update-cart request may have superseded this one while
          // GetProductImageUrls was resolving - re-check before applying.
          if (requestId !== latestUpdateCartRequestId) {
            logger.debug('updateCartData discarding stale response after image fetch', requestId, latestUpdateCartRequestId)
            return
          }

          tabProp.productImageUrls = updatedImageUrls
          tabProp.cart = updatedCart
        }
      }

      logger.debug('updateCartData done - carts updated in place')
    }
  } catch (error) {
    logger.error('updateCartData failed:', error)
    await handleError(error, t)
  }
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', 'AoePartyOrderEntry', event, selectedTab)
  updateCartData()
}

function updateProductEntryTabHandler(selectedProdEntryTab: number) {
  logger.debug('updateProductEntryTabHandler', 'AoePartyOrderEntry', selectedProdEntryTab)
  selectedProductEntryTab.value = selectedProdEntryTab
}

function updateSelectedCategoryHandler(selectedProdEntryCat: string) {
  logger.debug('updateSelectedCategoryHandler', 'AoePartyOrderEntry', selectedProdEntryCat)
  selectedProductEntryCategory.value = selectedProdEntryCat
}

function addGuestButtonClickHandler(event: MouseEvent) {
  logger.debug('addGuestButtonClickHandler', 'AoePartyOrderEntry', event)
  selectedTab.value = 1;
}

function GetShipNowHoverTitle(cart: IAoeCart | undefined) : string
{
  if (cart != undefined)
  {
    if (cart.items.length == 0)
    {
      return t('partyOrderEntry.ShipNowHoverTitleNoItems')
    }
    if (cart.paymentTotal == 0)
    {
      return t('partyOrderEntry.ShipNowHoverTitleNoPayment')
    }
    if ((cart.balance ?? 0) > 0)
    {
      return t('partyOrderEntry.ShipNowHoverTitleBalance')
    }
  }
  return ''
}

function submitShipNowCart(event: MouseEvent, cart: IAoeCart | undefined) {
  logger.debug('submitShipNowCart', event, cart)

  if (cart != undefined)
  {
    SetIsLoading(true)
    EventShipNowCartSubmit(
      {
        aoeCartIdToken: cart.aoeCartIdToken,
        aoeMasterOrderId: cart.aoeMasterOrderId
      }
    ).then(async (resp) => {
      logger.debug('EventShipNowCartSubmit', resp)
      if (resp.aoeOrderId)
      {
        Swal.fire({
          title: t("alerts.ShipNowSuccessTitle"),
          text: t("alerts.ShipNowSuccess", [
            resp.aoeOrderId
          ]),
          icon: "success",
          confirmButtonText: t("buttons.Okay"),
        }).then(() => {
          SetIsLoading(false)
          // Reload the page to remove the submitted cart and prevent duplicate submissions
          // This happens regardless of how the user dismisses the alert (click OK, click outside, or press ESC)
          window.location.reload()
        })
      }
      else
      {
        const errorCart = resp.item.carts.find(x => x.aoeCartIdToken == cart.aoeCartIdToken)
        if (errorCart) AlertCartSubmitResponse(t, errorCart)
        SetIsLoading(false)
      }
    }).catch(async error => {
        SetIsLoading(false)
        await handleError(error, t)
      })
  }
}

function GetSubmitHoverTitle() : string
{
  logger.debug('GetSubmitHoverTitle')
  return ''
}

function getStatusOfSubmitButton() : boolean
{
  logger.debug('getStatusOfSubmitButton')
  return false
}

function submitMasterOrder(event: MouseEvent) {
  logger.debug('submitMasterOrder', event)

  if (eventMO.value == undefined) return

  SetIsLoading(true)
    EventMasterOrderSubmit(
      {
        aoeMasterOrderIdToken: eventMO.value.item.aoeMasterOrderIdToken
      }
    ).then(async (resp) => {
      logger.debug('EventMasterOrderSubmit', resp)
      if (resp.orders != undefined && resp.orders.length > 0)
      {
        Swal.fire({
          title: t("alerts.PartyOrderSubmitSuccessTitle"),
          text: t("alerts.PartyOrderSubmitSuccess", [
          ]),
          icon: "success",
          confirmButtonText: t("buttons.Okay"),
        }).then((result) => {
          if (result.isConfirmed && eventMO.value != undefined) {
            SetIsLoading(false)
            //redirect to order summary page
            const receiptURL = orderReceiptUrl.value + '?masterorder=true&id=' +  eventMO.value.item.aoeMasterOrderIdToken;
              window.location.href = receiptURL
          }
        });
      }
      else
      {
        AlertMasterOrderSubmitResponse(t, resp.item.carts)
        SetIsLoading(false)
      }
    }).catch(error => {
        SetIsLoading(false)
        let messageKey = "";
        let messageText = ''
         if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;

          if (axiosError.response && axiosError.response.data) {
            const errorResponse = axiosError.response.data as IErrorResponse;
            if (
                  errorResponse &&
                  errorResponse.responseStatus &&
                  errorResponse.responseStatus.errorCode
                ) {
                    messageKey = errorResponse.responseStatus.errorCode;
                }
            }
          }
          if (messageKey == 'SfaEventNotFoundException') {
             messageText += `${t('alerts.CartSubmit.NotBalanced')}`
              Swal.fire({
                html: messageText,
                icon: 'error',
              })
          }
          else
          {
            handleError(error, t)
          }
      })
}

async function consolidatedAddressChangeHandler(conAddOpt: ConsolidatedAddressOptions) {
  logger.debug('consolidatedAddressChangeHandler', conAddOpt, 'changeAddressType before:', changeAddressType.value)
  selectedConsolidatedAddress.value = conAddOpt
  // Ensure the "existing" radio button remains selected when dropdown changes
  changeAddressType.value = 'existing'

  // Force re-render of radio buttons by changing the key
  radioButtonKey.value++

  // Use nextTick to ensure DOM updates
  await nextTick()
  logger.debug('consolidatedAddressChangeHandler', 'changeAddressType after:', changeAddressType.value, 'key:', radioButtonKey.value)
}

function addressControlUpdateHandler(data: IAddress)
{
  logger.debug('addressControlUpdateHandler', data)

  eventAddressFirstName.value = data.firstName ?? ''
  eventAddressLastName.value = data.lastName ?? ''
  eventAddressLine1.value = data.addressLine1
  eventAddressLine2.value = data.addressLine2
  eventAddressHouseNumber.value = data.houseNumber
  eventAddressCity.value = data.city
  eventAddressStateCode.value = data.stateCode
  eventAddressPostalCode.value = data.postalCode
  eventAddressCountryCode.value = data.countryCode
}

function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')

  addAddress(true).then(() => {
    CreateEventAddress(
        {
          eventIdToken: eventMO.value?.item.event.eventIdToken ?? '',
          addressId: eventAddressId.value ?? 0,
          aoeMasterOrderIdToken: eventMO.value?.item.aoeMasterOrderIdToken ?? '',
        }
      ).then(() => {
        LoadMasterOrder()
        closeChangeAddressPopup()
      })
  })
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  logger.debug('useSelectedAddressClick', selectedAddress)

  eventAddressLine1.value = selectedAddress.addressLine1
  eventAddressLine2.value = selectedAddress.addressLine2
  eventAddressHouseNumber.value = selectedAddress.houseNumber
  eventAddressCity.value = selectedAddress.city
  eventAddressStateCode.value = selectedAddress.stateCode
  eventAddressPostalCode.value = selectedAddress.postalCode
  eventAddressCountryCode.value = selectedAddress.countryCode

  addAddress(false).then(() => {
    if (verifyAddress.value.length > 0  || addressVerifyError.value == true) {
      addressVerificationShow.value = true;
    }
    else
    {
      CreateEventAddress(
        {
          eventIdToken: eventMO.value?.item.event.eventIdToken ?? '',
          addressId: eventAddressId.value ?? 0,
          aoeMasterOrderIdToken: eventMO.value?.item.aoeMasterOrderIdToken ?? '',
        }
      ).then(() => {
        LoadMasterOrder()
        closeChangeAddressPopup()
      })
    }
  })
}

function resetChangeAddressPopup() {
  logger.debug('resetChangeAddressPopup')

  // Reset radio button selection to default
  changeAddressType.value = 'existing'

  // Reset dropdown selection
  selectedConsolidatedAddress.value = { text: '', id: '' }

  // Reset all address form fields
  eventAddressFirstName.value = ''
  eventAddressLastName.value = ''
  eventAddressLine1.value = ''
  eventAddressLine2.value = ''
  eventAddressHouseNumber.value = ''
  eventAddressCity.value = ''
  eventAddressStateCode.value = ''
  eventAddressPostalCode.value = ''
  eventAddressCountryCode.value = ''

  // Reset error states
  showChangeAddressError.value = false

  // Re-enable body scrolling
  document.body.style.overflow = ''
}

function openChangeAddressPopup() {
  logger.debug('openChangeAddressPopup')
  showChangeAddress.value = true

  // Disable body scrolling when popup is open
  document.body.style.overflow = 'hidden'
}

function closeChangeAddressPopup() {
  logger.debug('closeChangeAddressPopup')
  showChangeAddress.value = false
  resetChangeAddressPopup()
}
</script>

<template>
  <!-- Gray overlay background -->
  <!-- Debug: showChangeAddress = {{ showChangeAddress }} -->
  <Teleport to="body">
    <div v-if="showChangeAddress" class="aoe-popup-overlay" @click="closeChangeAddressPopup()"></div>
  </Teleport>

  <div class="party-order-entry-div">
    <div class="breadcrumb">
      <div>
        <a :href="eventListURL" class="eventListLink">{{
          t('eventManagement.EventList')
        }}</a>
      </div>
      <div>&nbsp;/&nbsp;</div>
      <div>
        <a :href="eventManagementURL + (eventMO?.item.event.eventIdToken ?? '')" class="eventListLink">{{
          t('eventManagement.BreadCrumbPage')
        }}</a>
      </div>
      <template v-for="(segment, index) in breadcrumbStructure.segments" :key="index">
        <div>&nbsp;/&nbsp;</div>
        <div>
          <a v-if="segment.isLink"
             @click="segment.targetTab !== undefined && navigateToBreadcrumbTab(segment.targetTab)"
             class="eventListLink"
             style="cursor: pointer;">
            {{ segment.text }}
          </a>
          <span v-else>{{ segment.text }}</span>
        </div>
      </template>
    </div>
    <kTabStrip
      :selected="selectedTab"
      @select="onSelectHandler($event)"
      :tabs="tabStrip"
      class="party-order-entry-tabs"
      id="partyOrderEntryTabs"
      tab-position="left">
      <template v-slot:OrderSummaryTabStripHeader>
        <div class="party-order-entry-tab-header party-order-entry-tab-title-order-summary">
          {{ t('partyOrderEntry.OrderSummary') }}
        </div>
      </template>
      <template v-slot:GuestAddTabStripHeader>
        <div class="party-order-entry-tab-header party-order-entry-tab-title-guest-add">
          {{ t('partyOrderEntry.GuestListAdd') }}
        </div>
      </template>
      <template v-slot:GuestTabStripHeader="{props}">
        <div class="party-order-entry-tab party-order-entry-tab-title-guest">
          {{ props.title }}
        </div>
      </template>
      <template v-slot:HostSummaryTabStripHeader>
        <div class="party-order-entry-tab-header party-order-entry-tab-title-host-summary">
          {{ t('partyOrderEntry.HostSummary') }}
        </div>
      </template>
      <template v-slot:HostTabStripHeader="{props}">
        <div class="party-order-entry-tab party-order-entry-tab-title-host">
          {{ props.title }}
        </div>
      </template>
      <template v-slot:EventLocationTabStripHeader="{}">
        <div class="party-order-entry-tab party-order-entry-tab-title-event-location">
          <div class="consolidated-address-title">{{ t('partyOrderEntry.ConsolidatedAddress') }}</div>
          <div class="consolidated-address">{{ eventMO != undefined && eventMO.item.event.location != undefined ? eventMO.item.event.location.addressLine1 + (eventMO.item.event.location.houseNumber ? ' ' + eventMO.item.event.location.houseNumber : '') : '' }}</div>
          <div class="consolidated-address">{{ eventMO != undefined && eventMO.item.event.location != undefined && eventMO.item.event.location.addressLine2 ? eventMO.item.event.location.addressLine2 : '' }}</div>
          <div class="consolidated-address">{{ eventMO != undefined && eventMO.item.event.location != undefined ? eventMO.item.event.location.city + (eventMO.item.event.location.stateCode && eventMO.item.event.location.stateCode !== 'N/A' ? ', ' + eventMO.item.event.location.stateCode : '') + (eventMO.item.event.location.postalCode ? ' ' + eventMO.item.event.location.postalCode : '') : '' }}</div>
          <div class="text-center consolidated-address-change-button">
            <KButton
              :fill-mode="'link'"
              @click="openChangeAddressPopup()"
            >
              {{ t('partyOrderEntry.ChangeAddressButton') }}
            </KButton>
          </div>
        </div>
      </template>
      <template v-slot:PartyOrderSummary>
        <div class="order-summary-template">
          <div class="row">
            <div class="col-12 order-summary-div">
              <AoePartyOrderSummary
                v-if="eventMO != undefined"
                :eventorder="eventMO"
                :hostonly="false"
                @add-Guest-Button-Click="addGuestButtonClickHandler"
                @update-cart="updateCartHandler"
              ></AoePartyOrderSummary>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 aoe-payment-control-order-summary-div">
              <AoePaymentControl
                :carts="eventMO?.item.carts ?? null"
                :showinvoice="false"
                :showdeletebutton="false"
                :allowaddpayment="false">
              </AoePaymentControl>
            </div>
          </div>
          <div class="row mt-3">
            <div ref="submintDiv" class="col-12 aoe-order-submit-div">
              <span :title="GetSubmitHoverTitle()" style="display: inline-block; pointer-events: auto;">
                <KButton
                  :rounded="'large'"
                  :disabled="getStatusOfSubmitButton()"
                  class="baseBtn primaryBtn submit-button"
                  @click="submitMasterOrder($event)">
                  {{ t('partyOrderEntry.SubmitButton') }}
                </KButton>
              </span>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:AddGuest>
        <div class="add-guest-template">
          <div class="row">
            <div class="col-12 add-guest-div">
              <AoeAddGuestControl
                v-if="eventMO != undefined"
                :eventorder="eventMO"
                @update-cart="updateCartHandler"
              ></AoeAddGuestControl>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:HostOrderSummary>
        <div class="order-summary-template">
          <div class="row">
            <div class="col-12 order-summary-div">
              <AoePartyOrderSummary
                v-if="eventMO != undefined"
                :eventorder="eventMO"
                :hostonly="true"
                @add-Guest-Button-Click="addGuestButtonClickHandler"
                @update-cart="updateCartHandler"
              ></AoePartyOrderSummary>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:GuestCart="{props}">
        <div class="guestTemplate">
          <div class="row">
            <div class="col-12 guest-header-div">
              <AoeGuestHeader
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                @update-cart="updateCartHandler">
              </AoeGuestHeader>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 tabbed-product-entry-div">
              <AoeTabbedProductEntry
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :ordertypeid="orderTypeId"
                :showheader="true"
                :entryselectedtab="selectedProductEntryTab"
                :entryselectedcategory="selectedProductEntryCategory"
                @update-cart="updateCartHandler"
                @update-product-entry-tab="updateProductEntryTabHandler"
                @update-selected-category="updateSelectedCategoryHandler">
              </AoeTabbedProductEntry>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 cart-item-grid-div">
              <AoeCartItemGrid
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :productimageurls="tabProps.find(x => x.index == selectedTab)?.productImageUrls ?? {}"
                :editable="true"
                @update-cart="updateCartHandler">
              </AoeCartItemGrid>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 party-discount-control-div">
              <AoeDiscountControl
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :productimageurls="tabProps.find(x => x.index == selectedTab)?.productImageUrls ?? {}"
                @update-cart="updateCartHandler">
              </AoeDiscountControl>
            </div>
          </div>
          <div class="row mt-3 shipping-control-row">
            <div class="col-12 shipping-control-div">
              <AoeShippingControl
                :selectedcountry="tabProps.find(x => x.index == selectedTab)?.defaultCountryCode ?? ''"
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :showshiptotype="true"
                @update-cart="updateCartHandler">
              </AoeShippingControl>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 aoe-payment-control-div">
              <AoePaymentControl v-if="props.dataItem.index == selectedTab"
                :carts="[tabProps.find(x => x.index == selectedTab)?.cart ?? {} as IAoeCart]"
                :showdeletebutton="true"
                :showinvoice="true"
                :allowaddpayment="true"
                @update-cart="updateCartHandler">
              </AoePaymentControl>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 aoe-order-summary-div">
              <AoeOrderTotalsSummary
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null">
              </AoeOrderTotalsSummary>
            </div>
          </div>
          <div v-if="tabProps.find(x => x.index == selectedTab)?.cart.codeIdShipToType.codeId == 'SHIPNOW'" class="row mt-3">
            <div ref="shipNowDiv" class="col-12 aoe-order-ship-now-div">
              <span :title="GetShipNowHoverTitle(tabProps.find(x => x.index == selectedTab)?.cart)" style="display: inline-block; pointer-events: auto;">
                <KButton
                  :rounded="'large'"
                  :disabled="tabProps.find(x => x.index == selectedTab)?.cart.items.length == 0 ||
                              tabProps.find(x => x.index == selectedTab)?.cart.paymentTotal == 0 ||
                              ((tabProps.find(x => x.index == selectedTab)?.cart.balance ?? 0) > 0)"
                  class="baseBtn primaryBtn ship-now-cart-button"
                  @click="submitShipNowCart($event, tabProps.find(x => x.index == selectedTab)?.cart)">
                  {{ t('partyOrderEntry.ShipNowButton') }}
                </KButton>
              </span>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:HostCart="{props}">
        <div class="hostTemplate">
          <div class="row">
            <div class="col-12 guest-header-div">
              <AoeGuestHeader
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                @update-cart="updateCartHandler">
              </AoeGuestHeader>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 tabbed-product-entry-div">
              <AoeTabbedProductEntry
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :ordertypeid="orderTypeId"
                :showheader="true"
                :entryselectedtab="selectedProductEntryTab"
                :entryselectedcategory="selectedProductEntryCategory"
                @update-cart="updateCartHandler"
                @update-product-entry-tab="updateProductEntryTabHandler"
                @update-selected-category="updateSelectedCategoryHandler">
              </AoeTabbedProductEntry>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 cartItemGridDiv">
              <AoeCartItemGrid
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :productimageurls="tabProps.find(x => x.index == selectedTab)?.productImageUrls ?? {}"
                :editable="true"
                @update-cart="updateCartHandler">
              </AoeCartItemGrid>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 party-discount-control-div">
              <AoeDiscountControl
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :productimageurls="tabProps.find(x => x.index == selectedTab)?.productImageUrls ?? {}"
                @update-cart="updateCartHandler">
              </AoeDiscountControl>
            </div>
          </div>
          <div class="row mt-3 shipping-control-row">
            <div class="col-12 shipping-control-div">
              <AoeShippingControl
                :selectedcountry="tabProps.find(x => x.index == selectedTab)?.defaultCountryCode ?? ''"
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null"
                :showshiptotype="true"
                @update-cart="updateCartHandler">
              </AoeShippingControl>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 aoe-payment-control-div">
              <AoePaymentControl v-if="props.dataItem.index == selectedTab"
                :carts="[tabProps.find(x => x.index == selectedTab)?.cart ?? {} as IAoeCart]"
                :showdeletebutton="true"
                :showinvoice="true"
                :allowaddpayment="true"
                @update-cart="updateCartHandler">
              </AoePaymentControl>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 aoe-order-summary-div">
              <AoeOrderTotalsSummary
                :cart="tabProps.find(x => x.index == selectedTab)?.cart ?? null">
              </AoeOrderTotalsSummary>
            </div>
          </div>
          <div class="row mt-3">
            <div ref="shipNowDiv" class="col-12 aoe-order-ship-now-div">
              <span :title="GetSubmitHoverTitle()" style="display: inline-block; pointer-events: auto;">
                <KButton
                  :rounded="'large'"
                  :disabled="getStatusOfSubmitButton()"
                  class="baseBtn primaryBtn submit-button"
                  @click="submitMasterOrder($event)">
                  {{ t('partyOrderEntry.SubmitButton') }}
                </KButton>
              </span>
            </div>
          </div>
        </div>
      </template>
    </kTabStrip>
  </div>

  <Popup
    :show="showChangeAddress"
    :popup-class="'aoe-change-address-popup-content'"
  >
    <div class="row aoe-change-address-popup-content-close">
      <div class="col-12 closePopup">
        <KButton
            :fill-mode="'clear'"
            @click="closeChangeAddressPopup()"
            class="popupCloseButton"
        >
          &#x2715;
        </KButton>
      </div>
    </div>
    <div class="row mt-3 aoe-change-address-popup-content-header">
      <div class="col-12">
        {{ t('partyOrderEntry.ChangeAddressPopupHeader') }}
      </div>
    </div>
    <div class="row mt-3 aoe-change-address-popup-content-divider">
      <div class="col-12">
        <hr/>
      </div>
    </div>
    <div class="row mt-3 aoe-change-address-popup-content-subheader">
      <div class="col-12">
        {{ t('partyOrderEntry.ChangeAddressPopupSubHeader') }}
      </div>
    </div>
    <div class="row mt-3 aoe-change-address-popup-content-option">
      <div class="col-1">
        <RadioButton
          :key="`existing-${radioButtonKey}`"
          :name="'group1'"
          size="large"
          :checked="changeAddressType === 'existing'"
          @change="() => { changeAddressType = 'existing'; logger.debug('Radio button clicked: existing') }"
          >
        </RadioButton>
      </div>
      <div class="col-11">
        <DropDownList
          :data-items='consolidatedAddressOptions'
          :value= "selectedConsolidatedAddress"
          :text-field="'text'"
          :data-item-key="'id'"
          @change="consolidatedAddressChangeHandler($event.target.value)">
        </DropDownList>
      </div>
    </div>
    <div class="row mt-3 aoe-change-address-popup-content-option">
      <div class="col-4">
        <RadioButton
          :key="`new-${radioButtonKey}`"
          :name="'group1'"
          size="large"
          :checked="changeAddressType === 'new'"
          @change="() => { changeAddressType = 'new'; logger.debug('Radio button clicked: new') }"
          :label="t('partyOrderEntry.ChangeAddressNewAddress')"
        >
        </RadioButton>
      </div>
    </div>
    <div v-if="changeAddressType == 'new'" class="row">
      <div class="col-12 address-control-div">
        <AddressControl
          :countryCode="eventMO?.item.event.defaultCountryCode ?? ''"
          :addressLine1="null"
          :addressLine2="null"
          :houseNumber="null"
          :city="null"
          :stateCode="null"
          :postalCode="null"
          :isdisabled="false"
          :columnClass="'col-8'"
          :showfullnamefield="true"
          :name="null"
          @address-change="addressControlUpdateHandler">
        </AddressControl>
      </div>
    </div>
    <div v-if="showChangeAddressError" class="row mt-3">
      <div class="col-12 error-consolidated-address">
        {{ t('partyOrderEntry.ChangeAddressError') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 text-center address-control-save-div">
        <KButton
          @click="addEventAddress($event)"
          class="baseBtn primaryBtn save-address-button"
          :rounded="'large'">
          {{ t('partyOrderEntry.SaveAddressButton') }}
        </KButton>
      </div>
    </div>
  </Popup>

  <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
    <VerifyAddressPopup
      :enteredAddress="{
        addressLine1: eventAddressLine1,
        addressLine2: eventAddressLine2,
        houseNumber: eventAddressHouseNumber,
        city: eventAddressCity,
        stateCode: eventAddressStateCode,
        postalCode: eventAddressPostalCode,
        countryCode: eventAddressCountryCode,
        firstName: eventAddressFirstName,
        lastName: eventAddressLastName,
      }"
      :verifyAddress="verifyAddress"
      :verifyAddressError="addressVerifyError"
      @use-entered-address="useEnteredAddressClick"
      @use-selected-address="useSelectedAddressClick"
      @re-enter-address="addressVerificationShow = false">
    </VerifyAddressPopup>
  </Popup>
</template>

<style lang="scss">

.breadcrumb {
  @include typography-body-large;
}

.party-order-entry-div {

  @media (max-width: 768px) {
    .k-tabstrip-items-wrapper {
      display: none;
    }

    .aoe-tabbed-product-entry {
      .single-line-quantity-input {
        margin-left: $spacing-1;
      }
    }
  }
}

.eventListLink {
  color: var(--primary-text-color, var(--vt-c-teal-dark));
  text-decoration: underline !important;
}

.error-consolidated-address
{
  color: red;
}

.hostTemplate .single-line-auto-complete-row,
.guestTemplate .single-line-auto-complete-row
{
  margin-top: 1rem !important;
}

.party-order-entry-tabs .k-tabstrip-content
{
  width: 85%;
}

.party-order-entry-tabs .k-animation-container
{
  width: 100%;
}

.party-order-entry-tabs .k-tabstrip-items-wrapper
{
  width: 15%;
}

.party-order-entry-section-header-div
{
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
  margin-left: 10px;
}

.grid-delete .k-svg-icon,
.grid-delete .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.grid-delete .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,
.percent-off-item-delete-icon .k-svg-icon,
.percent-off-item-delete-icon.k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.percent-off-item-delete-icon.k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 30px;
    height: 30px;
}


.aoe-cart-item-grid .k-grid-content,
.aoe-payment-grid .k-grid-content {
  overflow-y: hidden;
}

.aoe-cart-item-grid .k-grid-header,
.aoe-payment-grid .k-grid-header
{
  padding: inherit !important;
  padding-inline-end: inherit;
}

.aoe-order-ship-now-div,
.aoe-order-submit-div
{
  text-align: right;
}

.party-order-entry-tab,
.party-order-entry-tab-header
{
  @include typography-body;
  padding: $spacing-2-5;
}

.party-order-entry-tab-header
{
  @include font-weight-semibold;
}

.consolidated-address-title
{
  @include typography-body-large;
  @include font-weight-semibold;
  text-align: center;
  padding-bottom: 10px;
}

.consolidated-address
{
  @include typography-body;
  text-align: center;
}

.tabbed-product-entry-div .k-tabstrip-items-wrapper,
.tabbed-product-entry-div .k-animation-container,
.tabbed-product-entry-div .k-tabstrip-content
{
  width: 100%;
}

.tabbed-product-entry-div .k-tabstrip-items-wrapper
{
  @include typography-h4;
  @include font-weight-semibold;
}

.tabbed-product-entry-div .k-item
{
  padding: $spacing-2-5;
}


.inventory-stop-sell
{
  color: red;
}

.inventory-available
{
  color: green;
}

.inventory-low-stock
{
  color: yellow;
}

.inventory-out-of-stock
{
  color: orange;
}

.product-grid-stock-indicator .k-svg-icon,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,
.stock-indicator-key-div .k-svg-icon,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 40px;
    height: 40px;
}

.aoe-change-address-popup-content
{
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);
  z-index: $z-modal;
}

.closePopup
{
  text-align: right;
}

.aoe-change-address-popup-content-header
{
  @include typography-h4;
  text-align: center;
}

.aoe-change-address-popup-content-option label
{
  @include typography-small;
}

.aoe-popup-overlay
{
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(black, 0.5);
  z-index: $z-modal-backdrop;
  overflow: hidden;
}

</style>
